import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from './notify';
import { Receipt, Printer, X, Ban, CheckCircle, FileText } from 'lucide-react';
import { VisitCard, ItemsTable, money, itemsTotal } from './VisitBits';
import ConfirmDialog from '../../components/ConfirmDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BillingPanel({ visits, refresh, authCfg, role, isSuperAdmin }) {
  const canAct = isSuperAdmin || ['Reception', 'Finance', 'Admin'].includes(role);
  const queue = visits.filter((v) => v.status === 'READY_FOR_BILLING');

  const [active, setActive] = useState(null);
  const [billed, setBilled] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [dialog, setDialog] = useState(null);

  const billableItems = (v) => [
    ...(v.service_items || []),
    ...(v.lab_orders || []).filter((o) => o.status === 'completed'),
    ...(v.rad_orders || []).filter((o) => o.status === 'completed'),
    ...(v.dental_orders || []).filter((o) => o.status === 'completed'),
    ...(v.prescriptions || []).filter((p) => p.status === 'dispensed'),
  ];

  const generateClaim = () => {
    setDialog({
      title: 'Generate claim',
      description: `Generate claim for ${active.patient_name} — total ${money(active, itemsTotal(billableItems(active)))}? This will deduct the amount from the family balance.`,
      confirmLabel: 'Generate Claim',
      onConfirm: async () => {
        setSubmitting(true);
        try {
          const res = await axios.post(`${API}/visits/${active.visit_id}/bill`, {}, authCfg);
          setBilled(res.data);
          toast.success(`Claim ${res.data.claim_id} generated successfully`);
          refresh();
        } catch (e) {
          toast.error(e.response?.data?.detail || 'Failed to generate claim');
        } finally {
          setSubmitting(false);
        }
      },
    });
  };

  const cancelVisit = (visit) => {
    setDialog({
      title: 'Cancel visit',
      description: `Cancel the visit for ${visit.patient_name}? Nothing will be billed.`,
      withReason: true,
      confirmLabel: 'Cancel Visit',
      destructive: true,
      onConfirm: async (reason) => {
        try {
          await axios.post(`${API}/visits/${visit.visit_id}/cancel`, { reason: reason || '' }, authCfg);
          toast.success('Visit cancelled');
          setActive(null);
          refresh();
        } catch (e) {
          toast.error(e.response?.data?.detail || 'Failed to cancel visit');
        }
      },
    });
  };

  if (active) {
    const items = billableItems(active);
    return (
      <Card className="border-green-200 shadow-md">
        <CardHeader className="bg-gradient-to-r from-green-50 to-green-100 border-b border-green-200 p-4">
          <CardTitle className="flex items-center justify-between text-green-900 text-base">
            <span className="flex items-center gap-2"><Receipt className="w-5 h-5" /> Billing — {active.patient_name}</span>
            <Button variant="outline" size="sm" onClick={() => { setActive(null); setBilled(null); }} data-testid="billing-close">
              <X className="w-4 h-4 mr-1" /> Back to queue
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 space-y-4">
          <div className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
            {active.visit_id} • Serial: {active.patient_serial_number} • Family: {active.family_id}
            {(active.consultation?.final_diagnosis || active.consultation?.provisional_diagnosis || active.consultation?.diagnosis) && <span> • Diagnosis: <span className="font-medium text-gray-800">{active.consultation.final_diagnosis || active.consultation.provisional_diagnosis || active.consultation.diagnosis}</span></span>}
          </div>

          {billed ? (
            <div className="p-6 bg-green-50 border border-green-200 rounded-xl text-center space-y-3" data-testid="billing-success">
              <CheckCircle className="w-10 h-10 text-green-600 mx-auto" />
              <p className="text-lg font-bold text-green-800">Claim {billed.claim_id} generated</p>
              <p className="text-sm text-gray-600">
                Total: {active.currency_symbol}{Number(billed.total_amount).toFixed(2)} ({billed.currency_code}) • New family balance: ${Number(billed.new_balance).toFixed(2)} USD
              </p>
              <Button onClick={() => window.open(`/print/${billed.claim_id}`, '_blank')} className="bg-green-600 hover:bg-green-700" data-testid="billing-print-bill">
                <Printer className="w-4 h-4 mr-2" /> Print Bill
              </Button>
              <Button onClick={() => window.open(`/print-visit/${active.visit_id}`, '_blank')} variant="outline" className="ml-2 border-teal-300 text-teal-700 hover:bg-teal-50" data-testid="billing-print-visit-summary">
                <FileText className="w-4 h-4 mr-2" /> Print Visit Summary
              </Button>
              {active.admission?.discharged_at && (
                <Button onClick={() => window.open(`/print-discharge/${active.visit_id}`, '_blank')} variant="outline" className="ml-2 border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="billing-print-discharge">
                  Discharge Letter
                </Button>
              )}
            </div>
          ) : (
            <>
              <ItemsTable title="Services / Fees" items={active.service_items || []} visit={active} />
              <ItemsTable title="Lab Tests (completed)" items={(active.lab_orders || []).filter((o) => o.status === 'completed')} visit={active} showResult />
              <ItemsTable title="Radiology Scans (completed)" items={(active.rad_orders || []).filter((o) => o.status === 'completed')} visit={active} showResult />
              <ItemsTable title="Dental Investigations (completed)" items={(active.dental_orders || []).filter((o) => o.status === 'completed')} visit={active} showResult />
              <ItemsTable title="Medications (dispensed)" items={(active.prescriptions || []).filter((p) => p.status === 'dispensed')} visit={active} showInstructions />
              {(active.lab_orders || []).some((o) => o.status === 'rejected') && (
                <p className="text-xs text-red-600">Note: rejected lab tests are excluded from the bill.</p>
              )}
              {(active.prescriptions || []).some((p) => p.status === 'not_dispensed') && (
                <p className="text-xs text-red-600" data-testid="billing-not-dispensed-note">
                  Note: {(active.prescriptions || []).filter((p) => p.status === 'not_dispensed').length} medication(s) were not given by the pharmacy and are excluded from the bill.
                </p>
              )}
              <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                <span className="font-semibold text-green-900">Grand Total</span>
                <span className="text-xl font-bold text-green-900" data-testid="billing-grand-total">{money(active, itemsTotal(items))}</span>
              </div>
              <div className="flex gap-3">
                <Button onClick={generateClaim} disabled={submitting || items.length === 0} className="flex-1 bg-green-600 hover:bg-green-700 h-11" data-testid="billing-generate-claim">
                  {submitting ? 'Generating…' : 'Generate Claim'}
                </Button>
                <Button variant="outline" onClick={() => cancelVisit(active)} className="border-red-300 text-red-600 hover:bg-red-50 h-11" data-testid="billing-cancel-visit">
                  <Ban className="w-4 h-4 mr-1" /> Cancel Visit
                </Button>
              </div>
            </>
          )}
          <ConfirmDialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)} {...(dialog || {})} />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
        <Receipt className="w-5 h-5 text-green-600" /> Ready for Billing ({queue.length})
      </h2>
      {queue.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500" data-testid="billing-empty">
          No visits ready for billing. Completed visits will appear here.
        </div>
      )}
      {queue.map((v) => (
        <VisitCard
          key={v.visit_id}
          visit={v}
          showHospital={isSuperAdmin || role === 'ViewOnly'}
          actionLabel={canAct ? `Bill ${money(v, itemsTotal(billableItems(v)))}` : null}
          onAction={canAct ? () => { setActive(v); setBilled(null); } : null}
          actionTestId={`bill-${v.visit_id}`}
        />
      ))}
    </div>
  );
}
